// "Sender: Sam Lee · Writing for the first time" with one-click Allow / Screen out, shown on the
// thread drawer, the thread reader and a card's page. The same local decision as the screener,
// with the same undo. For list mail, the unsubscribe ladder sits underneath.
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { SenderView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { useSender } from "@web/lib/sendersQueries";
import { Button } from "@web/ui/Button";
import { showsLadder } from "@web/unsubscribe/unsubscribe";
import { UnsubscribeControl } from "@web/unsubscribe/UnsubscribeControl";
import { screenSender } from "./screenOps";
import { screenStateLabel, senderName, type ScreenTarget } from "./screener";

/** What to show for an address MeeMail has no stats for yet (the server creates it on decision). */
function unknownSender(target: ScreenTarget): SenderView {
  return {
    accountId: target.accountId,
    address: target.address,
    domain: target.address.slice(target.address.lastIndexOf("@") + 1),
    name: target.name,
    inboundN: 0,
    outboundN: 0,
    lastInAt: null,
    lastOutAt: null,
    avgReplyChars: null,
    isList: false,
    screen: "none",
    screenedAt: null,
    firstSeenAt: null,
    awaitingDecision: false,
    unsubscribe: { state: "none", rung: null, at: null, detail: null },
  };
}

export function SenderScreenLine({
  target,
  className,
}: {
  target: ScreenTarget;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const query = useSender(target.accountId, target.address);
  if (query.isPending || query.isError) return null;
  const sender = query.data ?? unknownSender(target);
  const name = senderName({ name: sender.name ?? target.name, address: sender.address });
  const decide = (screen: SenderView["screen"]) => void screenSender(queryClient, sender, screen);
  return (
    <div className={cn("flex flex-col gap-1", className)} data-testid="sender-screen-line">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-muted">Sender:</span>
        <span className="min-w-0 truncate font-medium text-ink" title={sender.address}>
          {name}
        </span>
        <span
          className={cn(
            // On a phone the state takes its own line, so no separator dot starts a line.
            "min-w-0 text-muted max-sm:basis-full",
            sender.awaitingDecision && "text-note-ink",
            sender.screen === "allowed" && "text-calm",
          )}
        >
          <span aria-hidden className="max-sm:hidden">
            ·{" "}
          </span>
          {screenStateLabel(sender)}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {sender.screen !== "allowed" ? (
            <Button size="sm" variant="ghost" onClick={() => decide("allowed")}>
              Allow
            </Button>
          ) : null}
          {sender.screen !== "screened_out" ? (
            <Button size="sm" variant="ghost" onClick={() => decide("screened_out")}>
              Screen out
            </Button>
          ) : null}
          {sender.awaitingDecision ? (
            <Link
              to="/screener"
              className="hidden px-1 text-xs text-muted underline underline-offset-2 hover:text-ink sm:inline"
            >
              New senders
            </Link>
          ) : null}
        </span>
      </div>
      {showsLadder(sender) ? (
        <UnsubscribeControl
          accountId={sender.accountId}
          address={sender.address}
          sender={sender}
          className="pb-1"
        />
      ) : null}
    </div>
  );
}
